import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Avatar,
	Box,
	Button,
	Flex,
	HStack,
	MaximizeOutline,
	MenuAlt2Outline,
	MinimizeOutline,
	SkeletonText,
	Spacer,
	Text,
	useToast,
	XOutline,
} from '@bonusly/design-system';

import type { BizyPeopleBrowserEntry, BizyPersonMoment } from '../../../lib/bizy/types';
import { useBizyMarkMomentSeenMutation, useBizyMomentFeedbackMutation, usePersonMoments } from '../../../queries/bizy-people';
import { Bizy } from '../../bizy';
import { RelativeTime } from '../../datetime';
import { BIZY_CHAT_HEADER_HEIGHT, BizyHeaderIconButton, BizySidePanelHeader } from '../bizy-side-panel-header';
import { useBizyMomentDeck } from '../hooks/use-bizy-moment-deck';
import { useBizyPeopleEntries } from '../hooks/use-bizy-people-entries';
import { DeckCardPresence, getDeckCardVariants } from '../lib/bizy-deck-animation';
import { deckCardLayer, deckPose, deckRestPose, type DeckDirection } from '../lib/bizy-deck-geometry';
import { buildRecognizeMomentChatPayload } from '../lib/build-recognize-moment-chat-payload';
import { hasShownBizyDeckNavigationHint, markBizyDeckNavigationHintShown } from '../lib/panel-preferences';
import { useBizyPendingChatMessageStore } from '../store/use-bizy-pending-chat-message-store';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { DeckNavigationButton, ShortcutKey } from './bizy-deck-navigation-button';
import { BizyFeedbackModal, type BizyFeedbackCategory } from './bizy-feedback-modal';
import { BizyMessageFeedback } from './bizy-message-feedback';
import { BizySourceReferencePopover } from './bizy-source-reference-popover';

// Person detail view (BIZY-637). Shown in the main column in place of the chat
// when a People Browser entry is opened. Shows the active moment as a card
// (avatar/name/time, title + description, source, x-of-y, Skip / Let's dive in)
// and the person's previous moments in an accordion below. Skip advances through
// the sidebar's up-to-5 entries (snapshotted on open so the count is stable);
// the last Skip returns to the chat.

function toDate(raw: string | null | undefined): Date | undefined {
	if (!raw) return undefined;
	const d = new Date(raw);
	return Number.isNaN(d.getTime()) ? undefined : d;
}

// Previous-memories rows show this many entries until "All memories…" expands
// the list (BIZY-671).
const PREVIOUS_MEMORIES_PREVIEW_COUNT = 5;

// Negative-feedback categories tailored to an observed moment (BIZY-734).
// Unlike the chat-response defaults, these describe what's wrong with an
// observation (not a reply), so "Not what I asked for" / "Slow or buggy" are
// dropped and "Not relevant or noteworthy" is added. The `id`s must match
// `ObservedMoment::FEEDBACK_CATEGORIES` on the backend.
const MOMENT_FEEDBACK_CATEGORIES: readonly BizyFeedbackCategory[] = [
	{ id: 'inaccurate', labelKey: 'feedback.momentCategories.inaccurate' },
	{ id: 'not_relevant', labelKey: 'feedback.momentCategories.notRelevant' },
	{ id: 'tone', labelKey: 'feedback.momentCategories.tone' },
	{ id: 'safety', labelKey: 'feedback.momentCategories.safety' },
	{ id: 'other', labelKey: 'feedback.momentCategories.other' },
];

// Strip the person's leading first name from a memory headline and capitalize
// the verb that follows, e.g. "Kevin pushed your thinking…" → "Pushed your
// thinking…" (BIZY-671). Headlines that don't start with the first name (or
// where nothing follows it) pass through unchanged.
function stripLeadingFirstName(headline: string, personName: string): string {
	const firstName = personName.trim().split(/\s+/)[0];
	if (!firstName || !headline.startsWith(`${firstName} `)) return headline;
	const rest = headline.slice(firstName.length + 1).trimStart();
	return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : headline;
}

// A 100px-wide Bizy sits 68px outside the card, leaving a 32px overlap.
const WIDE_DECK_BIZY_OFFSET_X = 68;
const PERSON_VIEW_MAX_WIDTH = '680px';

// Soft ambient glow behind the card, from the BIZY-665 design (teal/50,
// blue/50, purple/100 ellipses blurred 100px over the surface).
const GLOW_BLOBS = [
	{ bg: 'teal.50', top: '8%', left: '2%', w: '55%', h: '45%' },
	{ bg: 'blue.50', top: '0%', left: '22%', w: '55%', h: '45%' },
	{ bg: 'purple.100', top: '12%', left: '48%', w: '50%', h: '40%' },
];

interface BizyPersonViewProps {
	showFullscreenToggle?: boolean;
	// Desktop MFE (Electron): render as a full-surface view — the header doubles
	// as the window drag region and the top padding is trimmed so content sits at
	// the very top. Off by default so the web side panel is unchanged.
	fullSurface?: boolean;
	// Override for the close (X) control. The web panel closes the whole panel;
	// the desktop MFE (always full-window) just clears the viewed person instead.
	onClose?: () => void;
}

function BizyPersonViewScrollSurface({ usesOverlayScroll, fullSurface, children }: { usesOverlayScroll: boolean; fullSurface: boolean; children: ReactNode }) {
	if (usesOverlayScroll) {
		return (
			<Flex flex={1} minH={0}>
				<Box flex={1} minH={0} overflow="hidden">
					<Box
						data-component="bizy-person-scroll"
						h="100%"
						overflowY="auto"
						overflowX="hidden"
						overscrollBehavior="contain"
						pt={BIZY_CHAT_HEADER_HEIGHT}
						sx={{
							scrollbarGutter: 'stable',
							scrollbarWidth: 'none',
							'&::-webkit-scrollbar': { display: 'none' },
						}}
					>
						<Box px={8} pt={fullSurface ? 2 : 4} pb={4} position="relative" data-component="bizy-person-content">
							{children}
						</Box>
					</Box>
				</Box>
			</Flex>
		);
	}

	return (
		<Box flex={1} minH={0} overflowY="auto" overflowX="hidden" px={4} pt={4} pb={4} position="relative" data-component="bizy-person-content">
			{children}
		</Box>
	);
}

export function BizyPersonView({ showFullscreenToggle = true, fullSurface = false, onClose }: BizyPersonViewProps) {
	const { t } = useTranslation('admin_bizy');
	const viewedPerson = useBizySidePanelStore((state) => state.viewedPerson);
	const clearViewedPerson = useBizySidePanelStore((state) => state.clearViewedPerson);
	const viewPerson = useBizySidePanelStore((state) => state.viewPerson);
	const showHistory = useBizySidePanelStore((state) => state.showHistory);
	const isFullscreen = useBizySidePanelStore((state) => state.isFullscreen);
	const toggleFullscreen = useBizySidePanelStore((state) => state.toggleFullscreen);
	const closePanel = useBizySidePanelStore((state) => state.close);
	const isPeopleBrowserCollapsed = useBizySidePanelStore((state) => state.isPeopleBrowserCollapsed);
	const togglePeopleBrowserCollapsed = useBizySidePanelStore((state) => state.togglePeopleBrowserCollapsed);
	const sendChatMessage = useBizyPendingChatMessageStore((state) => state.sendChatMessage);

	const { track } = useAnalytics();
	const toast = useToast();
	const hasShownDeckNavigationHintRef = useRef<boolean>();
	if (hasShownDeckNavigationHintRef.current == null) {
		hasShownDeckNavigationHintRef.current = hasShownBizyDeckNavigationHint();
	}
	const { entries } = useBizyPeopleEntries(true);
	const { mutate: markSeen } = useBizyMarkMomentSeenMutation();
	const { mutate: submitMomentFeedback } = useBizyMomentFeedbackMutation();
	const { data: momentsData, isLoading: isLoadingMoments } = usePersonMoments(viewedPerson?.id);

	// Negative feedback opens the shared category/details modal before it
	// submits (full parity with message feedback); positive/clear submit
	// immediately. Keyed by observed moment id.
	const [feedbackMomentId, setFeedbackMomentId] = useState<string | null>(null);
	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

	// A `?bizy=noticed.<slug>` deep link (BIZY-680) opens the person with only an
	// id (from history state) — no moment selected. Once the moments load, default
	// to the newest so the card and paging have a concrete anchor, filling in the
	// display name from the same response. The URL keeps its cosmetic slug (the
	// moment is not encoded), and re-defaulting the same person is a no-op.
	useEffect(() => {
		if (!viewedPerson || viewedPerson.observedMomentId) return;
		const newestMoment = momentsData?.moments[0];
		if (!newestMoment) return;
		viewPerson({
			id: viewedPerson.id,
			name: viewedPerson.name || momentsData?.person?.name || '',
			observedMomentId: newestMoment.id,
		});
	}, [viewedPerson, momentsData, viewPerson]);

	// Snapshot the ordered sidebar entries on open so "x of y" and Skip stay
	// stable while marking-seen refetches the live list.
	const snapshotRef = useRef<BizyPeopleBrowserEntry[] | null>(null);
	if (snapshotRef.current === null && entries.length > 0) snapshotRef.current = entries;
	const snapshot = snapshotRef.current ?? entries;

	const activeObservedMomentId = viewedPerson?.observedMomentId;
	const currentIndex = snapshot.findIndex((e) => e.observedMomentId === activeObservedMomentId);
	const currentEntry = currentIndex >= 0 ? snapshot[currentIndex] : undefined;

	const rootRef = useRef<HTMLDivElement>(null);
	const isCompact = !isFullscreen && !fullSurface;
	const usesOverlayScroll = !isCompact;

	// The moment deck's state machine (hydration key, gesture flag, transition
	// registry, focus hand-off, swipe paging) lives in the hook; the view stays
	// presentational and consumes this bundle.
	const {
		deckKey,
		logicalDeckKey,
		deckDistance,
		currentDeckAnimation,
		transitionRegistry,
		transitionRegistryRef,
		deckBizyState,
		isDeckGestureActive,
		deckTransition,
		bizyGestureTransition,
		requestedDeckDirectionRef,
		pageDeck,
		handleCardPointerDown,
		handleCardPointerUp,
		handleCardPointerCancel,
	} = useBizyMomentDeck({ activeObservedMomentId, viewedPerson, currentIndex, snapshot, viewPerson, isCompact, rootRef });

	// "All memories…" expansion is per card: paging or skipping to another
	// moment collapses the previous-memories list back to the preview. Reset
	// synchronously during render (not in an effect) so the first paint of the
	// next card never flashes the previous card's expanded list.
	const [showAllMemories, setShowAllMemories] = useState(false);
	const [expandedForMomentId, setExpandedForMomentId] = useState(activeObservedMomentId);
	if (expandedForMomentId !== activeObservedMomentId) {
		setExpandedForMomentId(activeObservedMomentId);
		setShowAllMemories(false);
	}

	// Mark the active moment seen the first time it's viewed, keyed by the
	// observed moment (BIZY-673): the backend opens the moment's notification
	// row, creating a pre-opened one for moments that never had a row, so the
	// sidebar's unread dot clears for every entry.
	const seenRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		if (!currentEntry || !currentEntry.unread) return;
		if (seenRef.current.has(currentEntry.observedMomentId)) return;
		seenRef.current.add(currentEntry.observedMomentId);
		markSeen(currentEntry.observedMomentId);
	}, [currentEntry, markSeen]);

	// Left/right arrows page through the deck (BIZY-665), stopping at the ends
	// rather than exiting. Keystrokes aimed at form fields or at focused
	// elements outside the person view (the main app stays interactive under
	// the compact panel) are left alone.
	useEffect(() => {
		const handleArrowKey = (event: KeyboardEvent) => {
			if (event.defaultPrevented || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
			const target = event.target instanceof HTMLElement ? event.target : null;
			if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
			if (target && target !== document.body && rootRef.current && !rootRef.current.contains(target)) return;
			const step: DeckDirection = event.key === 'ArrowRight' ? 1 : -1;
			const entry = snapshot[currentIndex + step];
			if (!entry) return;
			event.preventDefault();
			requestedDeckDirectionRef.current = step;
			viewPerson({ id: entry.person.id, name: entry.person.name ?? '', observedMomentId: entry.observedMomentId });
		};
		window.addEventListener('keydown', handleArrowKey);
		return () => window.removeEventListener('keydown', handleArrowKey);
	}, [snapshot, currentIndex, viewPerson, requestedDeckDirectionRef]);

	if (!viewedPerson) return null;

	const person = momentsData?.person ?? currentEntry?.person;
	const name = viewedPerson.name || person?.name || t('personView.unknownPerson');

	// Prefer the full moment record; fall back to the sidebar entry's headline so
	// the card still renders if the per-person endpoint fails (while loading, the
	// card shows a skeleton instead of this fallback).
	const activeMoment: Partial<BizyPersonMoment> | undefined =
		momentsData?.moments.find((m) => m.id === activeObservedMomentId) ??
		(currentEntry ? { headline: currentEntry.headline, moment: null, source: currentEntry.source, createdAt: currentEntry.sentAt } : undefined);

	const title = activeMoment?.headline ?? currentEntry?.headline ?? '';
	const body = activeMoment?.moment ?? '';
	const source = activeMoment?.source ?? currentEntry?.source ?? null;
	const activeReferences = activeMoment?.references ?? null;
	const momentDate = toDate(activeMoment?.createdAt ?? currentEntry?.sentAt);

	// Feedback controls copy the headline + body and light the current thumb.
	// Normalize the wire's `null` (never rated) to `undefined` for the shared
	// component's prop.
	const feedbackContent = [title, body].filter(Boolean).join('\n\n');
	const momentFeedbackType = activeMoment?.feedbackType ?? undefined;

	const total = snapshot.length;
	const position = currentIndex >= 0 ? currentIndex + 1 : 1;
	// Up to two ghost cards behind the active one (per design, the 3rd+ card is
	// hidden), so the deck thins to one on the next-to-last card, none on the last.
	const remainingCards = Math.min(Math.max(total - position, 0), 2);
	// Both paging controls are disabled at their end of the deck, matching the
	// arrow keys, which no-op there. Neither one exits the view — leaving is the
	// header's job.
	const previousEntry = snapshot[currentIndex - 1];
	const nextEntry = snapshot[currentIndex + 1];

	const showDeckNavigationHint = () => {
		if (hasShownDeckNavigationHintRef.current) return;
		hasShownDeckNavigationHintRef.current = true;
		markBizyDeckNavigationHintShown();
		toast({
			title: (
				<Trans
					t={t}
					i18nKey="personView.keyboardHint"
					components={{
						previous: <ShortcutKey />,
						next: <ShortcutKey />,
					}}
				/>
			),
			status: 'info',
			duration: 5000,
		});
	};

	const handlePrev = () => {
		if (pageDeck(-1, true)) showDeckNavigationHint();
	};
	const handleNext = () => {
		if (pageDeck(1, true)) showDeckNavigationHint();
	};

	const handleDiveIn = () => {
		if (!activeObservedMomentId) return;
		sendChatMessage(
			buildRecognizeMomentChatPayload({
				name,
				title,
				body,
				source,
				personId: person?.id ?? viewedPerson.id,
				avatarUrl: person?.avatarUrl,
				observedMomentId: activeObservedMomentId,
				t,
			})
		);
		clearViewedPerson();
	};

	const backToList = () => {
		showHistory();
		clearViewedPerson();
	};

	// Feedback handlers mirror the message-feedback flow: positive/clear submit
	// immediately, negative defers to the modal. `personId` is `viewedPerson.id`
	// so the optimistic cache patch targets the same query key as
	// `usePersonMoments` above.
	const handlePositiveMomentFeedback = (observedMomentId: string) => {
		if (!viewedPerson) return;
		track(AnalyticsEvent.Bizy.FeedbackPositive, { source: 'noticed', path: window.location.pathname });
		submitMomentFeedback({ personId: viewedPerson.id, observedMomentId, feedbackType: 'positive' });
	};

	const handleNegativeMomentFeedback = (observedMomentId: string) => {
		track(AnalyticsEvent.Bizy.FeedbackNegativeStarted, { source: 'noticed', path: window.location.pathname });
		setFeedbackMomentId(observedMomentId);
		setIsFeedbackModalOpen(true);
	};

	const handleClearMomentFeedback = (observedMomentId: string) => {
		if (!viewedPerson) return;
		track(AnalyticsEvent.Bizy.FeedbackCleared, { source: 'noticed', path: window.location.pathname });
		submitMomentFeedback({ personId: viewedPerson.id, observedMomentId, feedbackType: null });
	};

	const handleFeedbackModalSubmit = (category: string, details?: string) => {
		if (!viewedPerson || !feedbackMomentId) return;
		track(AnalyticsEvent.Bizy.FeedbackNegativeSubmitted, { source: 'noticed', category, path: window.location.pathname });
		submitMomentFeedback(
			{
				personId: viewedPerson.id,
				observedMomentId: feedbackMomentId,
				feedbackType: 'negative',
				feedbackCategory: category,
				feedbackDetails: details,
			},
			{
				// Close only after the write lands (mirrors message feedback); on
				// failure the modal stays open with its selections so the user can retry.
				onSuccess: () => {
					setIsFeedbackModalOpen(false);
					setFeedbackMomentId(null);
				},
			}
		);
	};

	// Only show the controls once the active moment's full record has loaded (not
	// during the skeleton or a paging refetch). This also guarantees the moments
	// query cache exists, so the mutation's optimistic `setQueryData` patch
	// always lands instead of no-opping against an empty cache.
	const activeMomentLoaded = momentsData?.moments.some((m) => m.id === activeObservedMomentId) ?? false;
	const feedbackControls =
		activeObservedMomentId && activeMomentLoaded ? (
			<BizyMessageFeedback
				messageId={activeObservedMomentId}
				content={feedbackContent}
				feedbackType={momentFeedbackType}
				onPositiveFeedback={handlePositiveMomentFeedback}
				onNegativeFeedback={handleNegativeMomentFeedback}
				onClearFeedback={handleClearMomentFeedback}
				showCopy={false}
				gap={3}
				mt={0}
			/>
		) : null;

	// Previous moments = this person's other moments not already surfaced in the
	// sidebar's current set (and not the active one).
	const sidebarMomentIds = new Set(snapshot.map((e) => e.observedMomentId));
	const previousMoments = (momentsData?.moments ?? []).filter((m) => m.id !== activeObservedMomentId && !sidebarMomentIds.has(m.id));
	const visiblePreviousMoments = showAllMemories ? previousMoments : previousMoments.slice(0, PREVIOUS_MEMORIES_PREVIEW_COUNT);
	const hasMorePreviousMoments = !showAllMemories && previousMoments.length > PREVIOUS_MEMORIES_PREVIEW_COUNT;

	return (
		<Flex
			ref={rootRef}
			tabIndex={-1}
			outline="none"
			data-component="bizy-person-view"
			direction="column"
			h="100%"
			minH={0}
			bg="bg.surface"
			position="relative"
			overflowX="hidden"
			overflowY="hidden"
		>
			{/* Ambient glow behind the header and card (BIZY-665). */}
			<Box position="absolute" inset={0} pointerEvents="none" aria-hidden overflow="hidden" sx={{ '.chakra-ui-dark &': { opacity: 0.15 } }}>
				{GLOW_BLOBS.map((blob) => (
					<Box key={blob.bg} position="absolute" top={blob.top} left={blob.left} w={blob.w} h={blob.h} bg={blob.bg} borderRadius="full" filter="blur(100px)" />
				))}
			</Box>
			<BizySidePanelHeader
				overlay={usesOverlayScroll}
				pl={(isFullscreen && !isPeopleBrowserCollapsed) || fullSurface ? 5 : 3}
				isDragRegion={fullSurface}
				left={
					<HStack spacing={1} minW={0}>
						{/* Compact: opens the People list menu. Fullscreen: reopens the
						    collapsed People sidebar (hidden while the sidebar is visible).
						    Desktop full-surface navigates people via the app sidebar, so it
						    shows no header menu button at all. */}
						{!fullSurface && (!isFullscreen || isPeopleBrowserCollapsed) && (
							<BizyHeaderIconButton
								label={isFullscreen ? t('sidePanel.openBizyMenu') : t('personView.backToList')}
								icon={<MenuAlt2Outline boxSize={5} />}
								onClick={isFullscreen ? togglePeopleBrowserCollapsed : backToList}
								isNoDrag={fullSurface}
							/>
						)}
						<Text fontSize="md" fontWeight="semibold" color="text.default" noOfLines={1} mt={fullSurface ? 6 : undefined}>
							{t('menu.bizyNoticed')}
						</Text>
					</HStack>
				}
				right={
					<HStack spacing={0}>
						{showFullscreenToggle && (
							<BizyHeaderIconButton
								label={isFullscreen ? t('sidePanel.exitFullscreen') : t('sidePanel.fullscreen')}
								icon={isFullscreen ? <MinimizeOutline boxSize={4} /> : <MaximizeOutline boxSize={4} />}
								onClick={toggleFullscreen}
								isNoDrag={fullSurface}
							/>
						)}
						<BizyHeaderIconButton
							label={t('sidePanel.close')}
							icon={<XOutline boxSize={5} />}
							onClick={() => (onClose ? onClose() : closePanel())}
							isNoDrag={fullSurface}
						/>
					</HStack>
				}
			/>

			<BizyPersonViewScrollSurface usesOverlayScroll={usesOverlayScroll} fullSurface={fullSurface}>
				{/* mt leaves room for the ghost cards (and, compact, the bee) peeking
				    above the active card. */}
				<Box maxW={PERSON_VIEW_MAX_WIDTH} mx="auto" mt={isCompact ? 6 : 4} data-component="bizy-person-deck-column">
					<HStack align="flex-start" spacing={0} mx={isCompact ? 0 : 8} position="relative" data-component="bizy-person-deck">
						{/* Wide layout: the bee overlaps the card without participating in
					    layout, so the card itself stays centred in the deck column. */}
						{!isCompact && (
							<Box
								position="absolute"
								top={-2}
								left={`-${WIDE_DECK_BIZY_OFFSET_X}px`}
								zIndex={4}
								display={{ base: 'none', sm: 'block' }}
								pointerEvents="none"
								data-component="bizy-person-deck-mascot"
							>
								<motion.div
									animate={isDeckGestureActive ? { y: -4, rotate: 2, scale: 1.04 } : { y: 0, rotate: 0, scale: 1 }}
									transition={bizyGestureTransition}
								>
									<Bizy state={deckBizyState} size={100} />
								</motion.div>
							</Box>
						)}

						<Box flex={1} minW={0} position="relative">
							{/* Compact layout: the bee peeks over the card's top-right corner. */}
							{isCompact && (
								<Box position="absolute" top="-36px" right={3} zIndex={4} pointerEvents="none" data-component="bizy-person-deck-mascot">
									<motion.div
										animate={isDeckGestureActive ? { y: -3, rotate: -3, scale: 1.06 } : { y: 0, rotate: 0, scale: 1 }}
										transition={bizyGestureTransition}
									>
										<Bizy state={deckBizyState} size={56} />
									</motion.div>
								</Box>
							)}

							{/* The next (up to two) cards peek out behind the active one at 40%
						    opacity, each with a visible stable-random tilt and offset seeded
						    from its moment id. Deeper cards spread farther out and shrink a
						    touch more. Deepest renders first so the nearer card paints over it.
						    Ghosts use the exact transform that an incoming card starts from;
						    consuming one can therefore swap it for the real card without a
						    silhouette jump or a layout-triggering inset animation. */}
							{Array.from({ length: remainingCards }, (_, index) => {
								const depth = remainingCards - index;
								const seed = snapshot[currentIndex + depth]?.observedMomentId ?? `depth-${depth}`;
								const pose = deckPose(seed, depth);
								const restPose = deckRestPose(pose, isCompact);
								return (
									<motion.div
										key={seed}
										data-component="bizy-person-ghost-card"
										data-deck-card-key={seed}
										data-deck-depth={depth}
										initial={{ opacity: 0 }}
										animate={{ opacity: 1, ...restPose }}
										transition={deckTransition}
										style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
									>
										<Box position="absolute" inset={0} bg="bg.surface" opacity={0.4} borderRadius="xl" boxShadow="base" />
									</motion.div>
								);
							})}

							{/* Active moment card. Keyed on the moment so paging swaps it through
						    AnimatePresence: popLayout pulls the outgoing card out of flow so the
						    incoming one takes the space immediately instead of the two stacking. */}
							<AnimatePresence
								mode="popLayout"
								initial={false}
								custom={transitionRegistry}
								onExitComplete={() => {
									transitionRegistryRef.current = {};
								}}
							>
								<motion.div
									key={deckKey}
									custom={transitionRegistry}
									variants={getDeckCardVariants(deckKey, currentDeckAnimation)}
									initial="enter"
									animate="center"
									exit="exit"
									style={{ position: 'relative', zIndex: deckCardLayer(currentDeckAnimation.direction, 'incoming') }}
									data-component="bizy-person-deck-card"
									data-deck-card-key={logicalDeckKey}
									data-deck-direction={currentDeckAnimation.direction}
									data-deck-depth={deckDistance}
									data-deck-transition={currentDeckAnimation.transition.duration === 0 ? 'instant' : 'animated'}
								>
									<DeckCardPresence>
										<Box
											position="relative"
											borderRadius="xl"
											bg="bg.surface"
											boxShadow="base"
											px={isCompact ? 4 : 8}
											pt={isCompact ? 4 : 8}
											pb={isCompact ? 4 : 6}
											style={{ touchAction: isCompact ? 'pan-y' : 'auto' }}
											onPointerDown={handleCardPointerDown}
											onPointerUp={handleCardPointerUp}
											onPointerCancel={handleCardPointerCancel}
											data-component="bizy-person-moment-card"
											data-moment-id={activeObservedMomentId ?? undefined}
										>
											{isCompact ? (
												<HStack align="center" spacing={3}>
													<Avatar name={name} src={person?.avatarUrl ?? undefined} size="md" />
													<Box minW={0}>
														{/* RelativeTime renders an inline span, so as an inline box its
										    height comes from the parent's strut (the inherited 1.5 line
										    height at the parent's font size), not its own. Making it a
										    block lets its own line height govern and closes the gap. */}
														<Text size="md" variant="strong" color="text.default" noOfLines={1}>
															{name}
														</Text>
														{momentDate && <RelativeTime date={momentDate} format="short" fontSize={1} color="text.softest" display="block" />}
													</Box>
												</HStack>
											) : (
												<HStack align="center" spacing={2}>
													<Avatar name={name} src={person?.avatarUrl ?? undefined} size="sm" />
													<Text size="lg" variant="strong" color="text.default" noOfLines={1}>
														{name}
													</Text>
													<Spacer />
													{momentDate && <RelativeTime date={momentDate} format="short" fontSize="sm" color="text.softest" />}
												</HStack>
											)}

											{/* Render the headline and body together once the full moment record
							    arrives; a skeleton bridges the fetch so the body doesn't pop in
							    under an already-visible headline. */}
											{isLoadingMoments ? (
												<SkeletonText mt={4} noOfLines={3} spacing={2} skeletonHeight="14px" />
											) : (
												<>
													{title && (
														<Text fontSize={isCompact ? 'sm' : 'md'} fontWeight="semibold" color="text.default" mt={3}>
															{title}
														</Text>
													)}
													{body && (
														<Text fontSize={isCompact ? 'sm' : 'md'} color="text.soft" mt={1} lineHeight="tall">
															{body}
														</Text>
													)}

													{isCompact
														? (source || feedbackControls) && (
																<HStack spacing={3} mt={3} minH={8} data-component="bizy-person-card-meta">
																	{source && (
																		<HStack spacing={1}>
																			<BizySourceReferencePopover source={source} references={activeReferences} />
																		</HStack>
																	)}
																	<Spacer />
																	{feedbackControls}
																</HStack>
															)
														: source && (
																<HStack spacing={1} mt={3}>
																	<BizySourceReferencePopover source={source} references={activeReferences} />
																</HStack>
															)}
												</>
											)}

											{isCompact ? (
												<Box mt={4}>
													<Button w="100%" variant="solid" colorScheme="primary" onClick={handleDiveIn} data-deck-action="draft">
														{t('personView.letsDraft')}
													</Button>
													{/* The stable controls sit outside the animated card. Reserve their
												    footer here so they remain visually inside the card. */}
													<Box mt={4} h={14} borderTopWidth="1px" borderColor="border.soft" />
												</Box>
											) : (
												<HStack
													align="center"
													mt={4}
													pt={4}
													borderTopWidth="1px"
													borderColor="border.soft"
													spacing={3}
													data-component="bizy-person-wide-footer"
												>
													<Box borderWidth="1px" borderColor="border.soft" borderRadius="full" px={3} py={0.5}>
														<Text fontSize="sm" fontWeight="medium" color="text.default" whiteSpace="nowrap">
															{t('personView.positionCount', { position, total })}
														</Text>
													</Box>
													<Spacer />
													{feedbackControls}
													{feedbackControls && <Box h={10} borderLeftWidth="1px" borderColor="border.soft" data-component="bizy-person-feedback-divider" />}
													<Button variant="solid" colorScheme="primary" size="md" onClick={handleDiveIn} data-deck-action="draft">
														{t('personView.letsDraft')}
													</Button>
												</HStack>
											)}
										</Box>
									</DeckCardPresence>
								</motion.div>
							</AnimatePresence>
							{isCompact ? (
								<HStack
									position="absolute"
									left="50%"
									bottom={4}
									transform="translateX(-50%)"
									spacing={3}
									zIndex={3}
									data-component="bizy-person-compact-navigation"
								>
									<DeckNavigationButton direction="prev" label={t('personView.prev')} placement="inline" isDisabled={!previousEntry} onClick={handlePrev} />
									<Text fontSize="md" fontWeight="medium" color="text.default" whiteSpace="nowrap">
										{t('personView.positionCount', { position, total })}
									</Text>
									<DeckNavigationButton direction="next" label={t('personView.next')} placement="inline" isDisabled={!nextEntry} onClick={handleNext} />
								</HStack>
							) : (
								<>
									<DeckNavigationButton direction="prev" label={t('personView.prev')} placement="edge" isDisabled={!previousEntry} onClick={handlePrev} />
									<DeckNavigationButton direction="next" label={t('personView.next')} placement="edge" isDisabled={!nextEntry} onClick={handleNext} />
								</>
							)}
						</Box>
					</HStack>
				</Box>

				{previousMoments.length > 0 && (
					<Box maxW={PERSON_VIEW_MAX_WIDTH} mx="auto" mt={14} data-component="bizy-person-memories">
						<Text fontSize="sm" fontWeight="semibold" color="text.default" mb={2}>
							{t('personView.previousMemories', { name })}
						</Text>
						{/* Keyed per card: the uncontrolled Accordion tracks its expanded row
						    by index, which would otherwise leak across paging and auto-open
						    an unrelated row on the next card. */}
						<Accordion key={activeObservedMomentId} allowToggle>
							{visiblePreviousMoments.map((moment) => {
								const when = toDate(moment.createdAt);
								return (
									<AccordionItem key={moment.id} borderColor="border.soft">
										{({ isExpanded }) => (
											<>
												<AccordionButton
													w="calc(100% + var(--chakra-space-4))"
													mx={-2}
													px={2}
													py={3}
													borderRadius="md"
													position="relative"
													_hover={{
														bg: 'secondary.bgAlpha',
														outline: '1px solid',
														outlineColor: 'secondary.bgAlpha',
														outlineOffset: 0,
														zIndex: 1,
													}}
												>
													<HStack flex={1} spacing={2} minW={0} textAlign="left">
														<Text size="sm" variant={isExpanded ? 'medium' : 'normal'} color="text.default" noOfLines={1} flex={1}>
															{moment.headline ? stripLeadingFirstName(moment.headline, name) : moment.headline}
														</Text>
														{when && <RelativeTime date={when} format="short" fontSize="xs" color="text.softest" />}
														<AccordionIcon color="text.softest" />
													</HStack>
												</AccordionButton>
												{(moment.moment || moment.source) && (
													<AccordionPanel px={0} pt={0} pb={3}>
														{moment.moment && (
															<Text fontSize="sm" color="text.softer" lineHeight="tall">
																{moment.moment}
															</Text>
														)}
														{moment.source && (
															<Box mt={moment.moment ? 2 : 0}>
																<BizySourceReferencePopover source={moment.source} references={moment.references} size="16px" showCount={false} />
															</Box>
														)}
													</AccordionPanel>
												)}
											</>
										)}
									</AccordionItem>
								);
							})}
						</Accordion>

						{hasMorePreviousMoments && (
							<Box pt={3}>
								<Button variant="link" size="sm" color="text.interactive" onClick={() => setShowAllMemories(true)}>
									{t('menu.allMemories')}
								</Button>
							</Box>
						)}
					</Box>
				)}
			</BizyPersonViewScrollSurface>

			<BizyFeedbackModal
				isOpen={isFeedbackModalOpen}
				onClose={() => {
					track(AnalyticsEvent.Bizy.FeedbackNegativeCancelled, { source: 'noticed', path: window.location.pathname });
					setIsFeedbackModalOpen(false);
					setFeedbackMomentId(null);
				}}
				onSubmit={handleFeedbackModalSubmit}
				categories={MOMENT_FEEDBACK_CATEGORIES}
				privacyNoticeKey="feedback.momentPrivacyNotice"
			/>
		</Flex>
	);
}
